import { Reveal } from "./Reveal";
import { renderBrandPhrases } from "./BrandLabel";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";

const FAQ_ITEMS = [
  {
    q: "How are products rated?",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan reads the ingredient list, understands it in context, cross-references it against the user's personal profile. Using advanced AI, it looks for allergens, preservatives, irritants, endocrine disruptors, potentially harmful chemicals and other ingredients users may prefer to avoid. It then gives a simple Safe, Caution or Avoid verdict based on what it can detect from the label you've scanned. Unlike many other ingredient scanners, the result is always based on what's in front of you, not what was in a database when the product was last updated.",
      },
      {
        type: "p",
        text: "For both Food and Body scans you get a plain English explanation of the verdict, a breakdown of any flagged ingredients and why they were flagged, a list of any allergens detected with an explanation and alternative product suggestions where helpful. Food scans also include a full nutrition summary covering calories, protein, carbs, fat, sugar and sodium, as well as a processing level explanation.",
      },
      {
        type: "p",
        text: "The app always reminds you to double-check the physical label in case anything was missed.",
      },
    ],
  },
  {
    q: "How are nutrition values calculated?",
    blocks: [
      {
        type: "p",
        text: "From analysing the Ingredient List, WhatThe? Scan uses AI to estimate the macronutrient profile based on the ingredients detected. These estimates are calculated per standard estimated serving size. Always refer to the Nutrition Panel on the Product for exact serving sizes.",
      },
    ],
  },
  {
    q: "How does the free trial work?",
    blocks: [
      {
        type: "p",
        text: "You get 4 free scans to use across both scanners when you first download the app. You won't be asked to join, sign up or create an account. Just open and start scanning.",
      },
    ],
  },
  {
    q: "Scanner purchase options",
    blocks: [
      {
        type: "p",
        text: "You can choose to buy WhatThe? Food or WhatThe? Body individually for $29.99 each — both include 1,000 lifetime scans. Or bundle both together for just $49.99 and get a generous 3,000 scans and a saving of $9.99. No subscriptions, no expiry. Affordable top-up packs are available if you ever need more.",
      },
    ],
  },
  {
    q: "What if I run out of scans?",
    blocks: [
      {
        type: "p",
        text: "Simply top up with one of our reasonably priced scan packs — Light (100 scans), Power (250 scans) or Bulk (500 scans). No subscription, no lock-in.",
      },
    ],
  },
  {
    q: "Does the app store my data?",
    blocks: [
      {
        type: "p",
        text: "Absolutely not. WhatThe? Scan doesn't require signing in, up or even out. We do not collect, track, store or sell your personal data.",
      },
    ],
  },
  {
    q: "What's the difference between the Food and Body scanners?",
    blocks: [
      {
        type: "p",
        text: "The Food scanner analyses ingredients in food products. The Body scanner analyses ingredients in personal care and cosmetic products. You can purchase one or both.",
      },
    ],
  },
  {
    q: "Which products does it work with?",
    blocks: [
      {
        type: "p",
        text: "Any product with a printed ingredients list — packaged food, drinks, snacks, skincare, haircare, cosmetics and more. If a label is damaged or hard to photograph because of the shape of the container (curved bottle?) you have the option to enter the ingredients manually or google and upload a photo of the list of ingredients from the internet.",
      },
    ],
  },
  {
    q: "How accurate is it?",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan uses AI to read and analyse ingredient lists — it doesn't match barcodes or return pre-stored scores. For most products with a clearly printed label, it is highly accurate.",
      },
      { type: "subhead", text: "Where it performs well" },
      {
        type: "list",
        items: [
          "Standard printed ingredient lists in good lighting",
          "Common additives, preservatives and E-numbers",
          "Allergens and sensitivities matched against your personal health profile",
          "Plain English explanations of complex ingredient names",
        ],
      },
      { type: "subhead", text: "Things that can affect your results" },
      {
        type: "list",
        items: [
          "Blurry, curved or low-light photos — the AI can only work with what it can read",
          "Non-Latin scripts (Chinese, Japanese, Arabic, Korean) on small or stylised print — these have slightly higher read errors than Latin-alphabet labels",
          "Region-specific or culturally unique ingredients with no clear English equivalent — described conservatively rather than precisely",
          "Generic terms like \u201cfragrance,\u201d \u201cspices\u201d or \u201cnatural flavour\u201d — these hide sub-ingredients the AI cannot identify",
          "Mixed-language labels where the same ingredient list appears in two languages — occasionally treated as duplicates rather than merged",
          "Handwritten or decorative fonts in any language",
        ],
      },
      {
        type: "tip",
        text: "Tip: if a photo is tricky, paste the ingredient list in manually for the most accurate result.",
      },
      { type: "subhead", text: "The honest bottom line" },
      {
        type: "p",
        text: "WhatThe? Scan is a decision-support tool, not a laboratory analysis or medical diagnosis. It gets the vast majority of common ingredients right and is transparent when it's uncertain. For serious allergies or medical decisions, always verify against a trusted independent source and consult a qualified healthcare professional.",
      },
      {
        type: "p",
        text: "For the best results, take a clear, well-lit photo of the full ingredient list — or paste it in manually if the label is tricky to photograph.",
      },
    ],
  },
  {
    q: "Is it available on Android?",
    blocks: [
      {
        type: "p",
        text: "WhatThe? Scan is currently available on iPhone only. Android is coming soon.",
      },
    ],
  },
  {
    q: "Can I use both scanners?",
    blocks: [
      {
        type: "p",
        text: "Yes. Purchase the 2 Scanner Bundle and get both Food and Body scanners at a discounted price.",
      },
    ],
  },
  {
    q: "What countries is it available in?",
    blocks: [
      { type: "p", text: "Currently available in Australia only." },
    ],
  },
];

const renderBlock = (block, i) => {
  if (block.type === "p") {
    return (
      <p key={i} className="text-[15px] leading-relaxed text-slate">
        {renderBrandPhrases(block.text)}
      </p>
    );
  }
  if (block.type === "subhead") {
    return (
      <p key={i} className="text-[15px] font-bold text-ink pt-1">
        {block.text}
      </p>
    );
  }
  if (block.type === "tip") {
    return (
      <p
        key={i}
        className="rounded-2xl border border-food-accent/20 bg-food-bg px-4 py-3 text-[15px] leading-relaxed text-food-text"
      >
        {block.text}
      </p>
    );
  }
  return (
    <ul key={i} className="space-y-2">
      {block.items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate">
          <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-food-accent" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

export const FAQ = () => (
  <section id="faq" className="relative py-20 md:py-28 bg-white border-y border-line scroll-mt-24">
    <div className="mx-auto max-w-[820px] px-5 md:px-8">
      <div className="mb-10 md:mb-14">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate mb-3">
            FAQ
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink leading-[1.1]">
            Frequently asked questions
          </h2>
          <p className="mt-4 text-[15px] md:text-base text-slate leading-relaxed">
            Everything you need to know about scans, privacy and how it all works.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.05}>
        <Accordion
          type="single"
          collapsible
          className="rounded-3xl border border-line bg-page px-5 md:px-7 shadow-soft"
          data-testid="faq-accordion"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-line last:border-b-0"
            >
              <AccordionTrigger className="py-5 text-base md:text-lg font-semibold text-ink hover:no-underline [&>svg]:text-food-accent">
                {renderBrandPhrases(item.q)}
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="space-y-4">
                  {item.blocks.map((block, bi) => renderBlock(block, bi))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
